import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { Link } from 'react-router-dom';
import styled, { CSSObject } from 'styled-components';
import {
  Table,
  TableIcon,
  ActionIconContainer,
  Card,
  SecondaryButton,
  AlertModal,
  Button,
  TextSort,
  EmptyData,
  MultiSelectInput,
  SubHeaderContainerProps,
  useCombinedUser,
  FlexColumn,
  Flex,
  useStyledTheme,
  WarningIcon,
  UserRoles,
} from '@equifinance/shared';
import { DateFormatStringEnum, formatDateTime, getDiff } from '@equifinance/shared/utils';
import moment from 'moment';
import { useCacheFilters } from '../../hooks/useCacheFilters';
import useApplicationApi from '../../api/useApplicationApi';
import {
  AuthorisationType, BrokerageSummary, CompanyStatus, useBrokeragesContext,
} from '../../contexts/brokerages';
import BrokerageStatusLabel from './BrokerageStatus';
import {
  ActionButton, CardContainer, Container, statusFilterCss as filterCss, SubHeaderContainer, SubHeaderFilters, WithdrawModalButtonContainer, WithdrawModalContainer,
} from '../CasesTable/CasesTable';

const mutateSearchValue = (value: string) => {
  const allowedChars = /[A-Za-z0-9\-\\/ ]/g;
  const filtered = value.match(allowedChars);
  return filtered ? filtered.join('') : '';
};

const BDMAssignedFilterContainer = styled.div`
  max-width: 90vw;
`;

const DateReceivedWrapper = styled(FlexColumn)<{ type: 'amber' | 'red' | 'default'}>`${({ theme, type }) => `
  margin: ${theme.padding[2]};
  max-width: ${theme.taskEvidenceStatusWidth};
  gap: ${theme.padding[1]};
  align-items: center;
  border-radius: ${theme.padding[3]};
  > i {
    display: flex;
  }
  ${type === 'amber' && `{
    background-color: ${theme.colours.warning[2]};
    padding: ${theme.padding[2]};
  }`};
  ${type === 'red' && `{
    background-color: ${theme.colours.danger[3]};
    padding: ${theme.padding[2]};
  }`};
`}`;

const IconContainer = styled(Flex)`${({ theme }) => `
  width: ${theme.padding[4]};
`}`;

const DateReceivedColumnContainer = styled(Flex)`
  width: 100%;
  max-width: 100%;
  align-items: center;
`;

const DatetimeText = styled.span`${({ theme }) => `
  flex-grow: 1;
  font-size: ${theme.fontSize[1]};
  color: ${theme.colours.neutral[2]};
`}`;

function AnnualReviewDateColumn(brokerage: BrokerageSummary) {
  const theme = useStyledTheme();
  const { nextAnnualReviewDate } = brokerage;
  const alert: 'amber' | 'red' | 'default' = useMemo(() => {
    if (!nextAnnualReviewDate) return 'default';
    const dateDiff = getDiff(new Date(nextAnnualReviewDate), moment.utc().startOf('d'), 'd', false);
    if (dateDiff < 0) {
      return 'red';
    }
    if (dateDiff <= 30) {
      return 'amber';
    }
    return 'default';
  }, [nextAnnualReviewDate]);

  const formattedAnnualReviewDate = useMemo(() => (
    formatDateTime(nextAnnualReviewDate, DateFormatStringEnum.DDMMYYYY)
  ), [nextAnnualReviewDate]);

  return (
    <DateReceivedColumnContainer>
      <DatetimeText>
        <DateReceivedWrapper type={alert}>
          {['amber', 'red'].includes(alert) && (
            <IconContainer>
              <WarningIcon
                stroke={alert === 'amber' ? theme.colours.warning[1] : theme.colours.danger[1]}
                fill={theme.colours.neutral[10]}
              />
            </IconContainer>
          )}
          {nextAnnualReviewDate ? formattedAnnualReviewDate : '-'}
        </DateReceivedWrapper>
      </DatetimeText>
    </DateReceivedColumnContainer>
  );
}

const PlainLink = styled(Link)`
  text-decoration: none;
`;

function ReactivateModalContent({ close }: { close: () => void }) {
  const onReactive = async () => {
    close();
  };

  return (
    <WithdrawModalContainer>
      <p>Are you sure you want to reactivate this account?</p>
      <WithdrawModalButtonContainer>
        <SecondaryButton small onClick={close}>Cancel</SecondaryButton>
        <Button small onClick={onReactive}>Reactivate</Button>
      </WithdrawModalButtonContainer>
    </WithdrawModalContainer>
  );
}

function DeactivateModalContent({ close }: { close: () => void }) {
  const onDeactive = async () => {
    close();
  };

  return (
    <WithdrawModalContainer>
      <p>Are you sure you want to deactivate this account?</p>
      <WithdrawModalButtonContainer>
        <SecondaryButton small onClick={close}>Cancel</SecondaryButton>
        <Button small onClick={onDeactive}>Deactivate</Button>
      </WithdrawModalButtonContainer>
    </WithdrawModalContainer>
  );
}

function SubHeader({ searchComponent, children }: SubHeaderContainerProps) {
  return (
    <SubHeaderContainer>
      <SubHeaderFilters>
        {searchComponent}
        {children}
      </SubHeaderFilters>
    </SubHeaderContainer>
  );
}

type BrokerMgmtFilterSettings = {
  statusFilter: string[];
  typeFilter: string[];
  bdmFilter: string[];
}

const defaultFilters: BrokerMgmtFilterSettings = {
  statusFilter: [],
  typeFilter: [],
  bdmFilter: [],
};

function BrokeragesTable() {
  const { brokeragesSummary, isFetchingBrokerages } = useBrokeragesContext();
  const [isFetchBDM, setIsFetchBDM] = useState(false);

  const { getInternalUsers } = useApplicationApi();
  const [BDMMap, setBDMMap] = useState<Record<string, string>>({});
  const { user } = useCombinedUser();

  useEffect(() => {
    (async () => {
      try {
        setIsFetchBDM(true);
        const BDMs = await getInternalUsers(UserRoles.BDM);
        setBDMMap(BDMs?.reduce((acc, { id, displayName }) => ({ ...acc, [id]: displayName }), {}) ?? {});
      } finally {
        setIsFetchBDM(false);
      }
    })();
  }, []);

  const deafultShowDeactiveModal = { id: '', open: false };
  const [showDeactivateModal, SetShowDeactivateModal] = useState<{ id: string, open: boolean }>(deafultShowDeactiveModal);
  const onClickDeactive = (id: string) => () => {
    SetShowDeactivateModal({ id, open: true });
  };

  const deafultShowReactivateModal = { id: '', open: false };
  const [showReactivateModal, SetShowReactivateModal] = useState<{ id: string, open: boolean }>(deafultShowReactivateModal);
  const onClickReactivate = (id: string) => () => {
    SetShowReactivateModal({ id, open: true });
  };

  const ActionButtons = useCallback(
    ({ id, status }: BrokerageSummary) => (
      <ActionIconContainer>
        {(status === CompanyStatus.Active || status === CompanyStatus.Rejected) && (
          <ActionButton data-testid={`btn-delete-${id}`} onClick={onClickDeactive(id)}>
            <TableIcon className="mi-delete" />
          </ActionButton>
        )}

        {status === CompanyStatus.Inactive && (
          <ActionButton data-testid={`btn-reactivate-${id}`} onClick={onClickReactivate(id)}>
            <TableIcon className="mi-refresh" />
          </ActionButton>
        )}

        <PlainLink data-testid={`btn-application-${id}`} to={`/manage-user/${id}`}>
          <ActionButton>
            <TableIcon className="mi-circle-arrow-right" />
          </ActionButton>
        </PlainLink>
      </ActionIconContainer>
    ),
    [brokeragesSummary],
  );

  const BrokerageStatus = useCallback(
    ({ status }: BrokerageSummary) => (
      <BrokerageStatusLabel status={status} />
    ),
    [brokeragesSummary],
  );

  const bdmAssignedSort = (rowA: BrokerageSummary, rowB: BrokerageSummary) => {
    const a = BDMMap[rowA.bdmAssigned];
    const b = BDMMap[rowB.bdmAssigned];

    if (a < b) {
      return -1;
    }

    if (a > b) {
      return 1;
    }

    return 0;
  };

  const columns = [
    {
      name: 'Registered company name',
      sortable: true,
      selector: (brokerage: BrokerageSummary) => brokerage.tradingCompanyName,
      sortFunction: TextSort<BrokerageSummary>('tradingCompanyName'),
    },
    {
      name: 'FCA number',
      sortable: true,
      selector: (brokerage: BrokerageSummary) => brokerage.fcaNumber,
      sortFunction: TextSort<BrokerageSummary>('fcaNumber'),
    },
    {
      name: 'Type',
      sortable: true,
      selector: (brokerage: BrokerageSummary) => AuthorisationType[brokerage.authorisationType],
      sortFunction: TextSort<BrokerageSummary>('authorisationType'),
    },
    {
      name: 'BDM assigned',
      sortable: true,
      selector: (brokerage: BrokerageSummary) => BDMMap[brokerage.bdmAssigned] || '-',
      sortFunction: bdmAssignedSort,
    },
    {
      name: 'Annual review date',
      sortable: true,
      cell: (brokerage: BrokerageSummary) => AnnualReviewDateColumn(brokerage),
      sortFunction: TextSort<BrokerageSummary>('nextAnnualReviewDate'),
    },
    {
      name: 'Status',
      sortable: true,
      cell: (brokerage: BrokerageSummary) => BrokerageStatus(brokerage),
      sortFunction: TextSort<BrokerageSummary>('status'),
    },
    {
      name: '',
      sortable: false,
      cell: (brokerage: BrokerageSummary) => ActionButtons(brokerage),
    },
  ];

  const stickyColumn = {
    style: {
      '&:is(:last-of-type)': {
        position: 'sticky',
        right: 0,
      },
    } as CSSObject,
  };

  const [filterStatus, setFilterStatus] = useState<string[]>([]);
  const [filterType, setFilterType] = useState<string[]>([]);
  const [filterBDM, setFilterBDM] = useState<string[]>([]);

  const { filters, updateFilters } = useCacheFilters<BrokerMgmtFilterSettings>({
    pageKey: 'borker-management-landing-page',
    userId: user?.id || '',
    defaultFilters,
  });

  // Restore Cached filters settings
  useEffect(() => {
    const { statusFilter, typeFilter, bdmFilter } = filters;
    setFilterStatus(statusFilter);
    setFilterType(typeFilter);
    setFilterBDM(bdmFilter);
  }, []);

  const filteredBrokerages = brokeragesSummary
    .filter((broker) => filterStatus.length === 0 || filterStatus.includes(broker.status))
    .filter((broker) => filterType.length === 0 || filterType.includes(broker.authorisationType))
    .filter((broker) => filterBDM.length === 0 || filterBDM.includes(broker.bdmAssigned)) ?? [];

  const subHeaderComponent = (
    <>
      <MultiSelectInput
        id="brokerage-status-filter"
        onChange={(value: string[]) => {
          setFilterStatus(value);
          updateFilters({ statusFilter: value });
        }}
        value={filterStatus}
        selectedLabel="Status"
        options={Object.keys(CompanyStatus).reduce((a, v) => ({ ...a, [v]: v }), {})}
        onDeselectOption={(value: string) => {
          const newValue = filterStatus.filter((s) => s !== value);
          setFilterStatus(newValue);
          updateFilters({ statusFilter: newValue });
        }}
        disabled={!brokeragesSummary.length}
        containerStyles={filterCss}
      />

      <MultiSelectInput
        id="broker-type-filter"
        onChange={(value: string[]) => {
          setFilterType(value);
          updateFilters({ typeFilter: value });
        }}
        value={filterType}
        selectedLabel="Type"
        options={Object.entries(AuthorisationType).reduce((a, [k, v]) => ({ ...a, [k]: v }), {})}
        onDeselectOption={(value: string) => {
          const newValue = filterType.filter((t) => t !== value);
          setFilterType(newValue);
          updateFilters({ typeFilter: newValue });
        }}
        disabled={!brokeragesSummary.length}
        containerStyles={filterCss}
      />

      <BDMAssignedFilterContainer>
        <MultiSelectInput
          id="bdm-assigned-filter"
          onChange={(value: string[]) => {
            setFilterBDM(value);
            updateFilters({ bdmFilter: value });
          }}
          value={filterBDM}
          selectedLabel="BDM"
          options={BDMMap}
          onDeselectOption={(value: string) => {
            const newValue = filterBDM.filter((t) => t !== value);
            setFilterBDM(newValue);
            updateFilters({ bdmFilter: newValue });
          }}
          disabled={!brokeragesSummary.length}
          containerStyles={filterCss}
        />
      </BDMAssignedFilterContainer>
    </>
  );

  return (
    <>
      <AlertModal
        title="Deactivate"
        content={(
          <DeactivateModalContent
            close={() => SetShowDeactivateModal(deafultShowDeactiveModal)}
          />
        )}
        isOpen={showDeactivateModal.open}
        onClose={() => SetShowDeactivateModal(deafultShowDeactiveModal)}
        showButtons={false}
      />

      <AlertModal
        title="Reactivate"
        content={(
          <ReactivateModalContent
            close={() => SetShowReactivateModal(deafultShowReactivateModal)}
          />
        )}
        isOpen={showReactivateModal.open}
        onClose={() => SetShowReactivateModal(deafultShowReactivateModal)}
        showButtons={false}
      />
      <Container>
        <Card>
          <CardContainer>
            <Table
              columns={columns}
              data={filteredBrokerages}
              styles={{
                headCells: stickyColumn,
                cells: stickyColumn,
              }}
              SubHeaderContainer={SubHeader}
              subHeaderComponent={subHeaderComponent}
              pagination
              defaultSortFieldId={1}
              defaultSortAsc={false}
              progressPending={isFetchingBrokerages || isFetchBDM}
              isFiltered={!!filterStatus.length || !!filterType.length}
              noDataComponent={<EmptyData message="Please start a new record." />}
              searchInputProps={{
                mutateValue: mutateSearchValue,
              }}
            />
          </CardContainer>
        </Card>
      </Container>
    </>
  );
}

export default BrokeragesTable;
